import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import {
  type CfitImage,
  type CfitOptionsWidth,
  extractCacheBustingParam,
  urlToCfit,
} from '@//utils/image/urlToCfit';

const assetPrefix = process.env.NEXT_PUBLIC_ASSETS_CDN || '';

const PRODUCT_CARD_WIDTHS: CfitOptionsWidth[] = [225, 375, 450, 600, 750, 900, 1200];

// Product cards render inside a 3:2 slot (.image { aspect-ratio: 3/2 }).
// Reserving the corresponding intrinsic height on the <img> lets the browser
// preloader allocate vertical space before the image decodes, avoiding the
// CLS that otherwise depends solely on the CSS aspect-ratio rule.
const PRODUCT_CARD_ASPECT_RATIO = 3 / 2;

// Presets describe the layout the card is rendered in so the browser can pick
// the right candidate from `srcSet`. Add a new entry here when a new layout
// context (different column count, different container) is introduced.
export const PRODUCT_CARD_IMAGE_PRESETS = {
  // Multi-row responsive grid — used by SearchResult, CampaignVehicles,
  // RetailersItemsSection. `Grid columns={3}` with auto-fill at min 300px,
  // capped to the 80rem container.
  productGrid: {
    widths: PRODUCT_CARD_WIDTHS,
    sizes:
      '(min-width: 1360px) 420px, (min-width: 696px) calc(50vw - 96px), (min-width: 592px) calc(100vw - 80px), calc(100vw - 32px)',
  },
  // Horizontal scrolling carousel — used by Startpage sections, RelatedVehicles
  // and article pages. `OverflowGrid columns={3}` shows 3 / 2 / 1.2 cards at
  // ≥ md / ≥ sm / < sm.
  overflowGrid: {
    widths: PRODUCT_CARD_WIDTHS,
    sizes:
      '(min-width: 1360px) 420px, (min-width: 896px) calc(33vw - 120px), (min-width: 592px) calc(50vw - 100px), calc((100vw / 1.2) - 40px)',
  },
} as const satisfies Record<string, { widths: CfitOptionsWidth[]; sizes: string }>;

export type ProductCardImagePreset = keyof typeof PRODUCT_CARD_IMAGE_PRESETS;

const useProductCardContainerImage = (
  item: ElasticListVehicle,
  loading: React.ImgHTMLAttributes<HTMLImageElement>['loading'],
  preset: ProductCardImagePreset = 'productGrid',
) => {
  const t = useTranslations('other.common');
  const { widths, sizes } = PRODUCT_CARD_IMAGE_PRESETS[preset];
  const image = useMemo(() => {
    const url = item.featuredImage?.files?.[0]?.url;
    const r = extractCacheBustingParam(item.featuredImage?.files?.[0]?.formats);
    if (url) {
      const cfit = urlToCfit(url, {
        width: 450,
        widths,
        sizes,
        alt: item.title,
        loading,
        r,
      });
      // Set the HTML height attribute from the card's display aspect ratio,
      // not from the CFIT URL — the URL stays width-only so the CDN preserves
      // the photo's true aspect (CSS object-fit: contain handles the rest).
      return { ...cfit, height: Math.round((cfit.width ?? 450) / PRODUCT_CARD_ASPECT_RATIO) };
    }
    const fallback: CfitImage = {
      src: `${assetPrefix}/images/placeholders/product-card.jpg`,
      srcSet: `${assetPrefix}/images/placeholders/product-card@2x.jpg 2x`,
      alt: t('imageMissing'),
      width: 450,
      height: 299,
    };
    return fallback;
  }, [item.featuredImage?.files, item.title, widths, sizes, loading, t]);
  return image;
};

export default useProductCardContainerImage;
